import { lazy, Suspense, type ReactNode } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "sonner";
import { ApiError } from "@/lib/api";
import { AuthProvider } from "@/auth/AuthContext";
import { ThemeProvider, useTheme } from "@/theme/ThemeContext";
import { FirstTimeThemeDialog } from "@/theme/FirstTimeThemeDialog";
import { AppErrorBoundary } from "@/components/layout/AppErrorBoundary";
import { CommandMenuProvider } from "@/components/shell/CommandMenu";
import { AppLayout } from "@/components/layout/AppLayout";
import { ProtectedRoute } from "@/components/layout/ProtectedRoute";
import { ConfirmDialogProvider } from "@/components/ui/confirm-dialog";
import { PageLoader } from "@/components/ui/page-loader";
// Eager-imported routes are the ones the typical signed-in trip-booking
// staffer hits straight after login. Everything else is split into its
// own chunk so the initial bundle stays trim — admin pages alone were
// pushing the main JS past 670 KB before this split.
import { FormPage } from "@/pages/FormPage";
import { BookedTripsPage } from "@/pages/BookedTripsPage";
import { LoginPage } from "@/pages/LoginPage";

// Auth flow lands once per user — fine to lazy-load.
const ConsumeMagicLinkPage = lazy(() =>
  import("@/pages/ConsumeMagicLinkPage").then((m) => ({
    default: m.ConsumeMagicLinkPage,
  })),
);
const AcceptInvitationPage = lazy(() =>
  import("@/pages/AcceptInvitationPage").then((m) => ({
    default: m.AcceptInvitationPage,
  })),
);
const ForgotPasswordPage = lazy(() =>
  import("@/pages/ForgotPasswordPage").then((m) => ({
    default: m.ForgotPasswordPage,
  })),
);
const ResetPasswordPage = lazy(() =>
  import("@/pages/ResetPasswordPage").then((m) => ({
    default: m.ResetPasswordPage,
  })),
);
const AccountPage = lazy(() =>
  import("@/pages/AccountPage").then((m) => ({ default: m.AccountPage })),
);

// Admin pages — only Admin / Super Admin reach these, so the typical
// User session never downloads them.
const AdminAuditPage = lazy(() =>
  import("@/pages/AdminAuditPage").then((m) => ({ default: m.AdminAuditPage })),
);
const AdminBillingPage = lazy(() =>
  import("@/pages/AdminBillingPage").then((m) => ({
    default: m.AdminBillingPage,
  })),
);
const AdminBookingsPage = lazy(() =>
  import("@/pages/AdminBookingsPage").then((m) => ({
    default: m.AdminBookingsPage,
  })),
);
const AdminDepartmentsPage = lazy(() =>
  import("@/pages/AdminDepartmentsPage").then((m) => ({
    default: m.AdminDepartmentsPage,
  })),
);
const AdminLocationsPage = lazy(() =>
  import("@/pages/AdminLocationsPage").then((m) => ({
    default: m.AdminLocationsPage,
  })),
);
const AdminMembersPage = lazy(() =>
  import("@/pages/AdminMembersPage").then((m) => ({
    default: m.AdminMembersPage,
  })),
);
const AdminReportsPage = lazy(() =>
  import("@/pages/AdminReportsPage").then((m) => ({
    default: m.AdminReportsPage,
  })),
);
const AdminTmsCleanupPage = lazy(() =>
  import("@/pages/AdminTmsCleanupPage").then((m) => ({
    default: m.AdminTmsCleanupPage,
  })),
);
const AdminUsersPage = lazy(() =>
  import("@/pages/AdminUsersPage").then((m) => ({ default: m.AdminUsersPage })),
);
const AdminPermissionsPage = lazy(() =>
  import("@/pages/AdminPermissionsPage").then((m) => ({
    default: m.AdminPermissionsPage,
  })),
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Don't retry client errors (4xx): they won't succeed on a retry, and a
      // 401 should surface immediately so the session-expiry redirect fires
      // fast instead of after a wasted second attempt. Retry once otherwise
      // (a transient network / 5xx blip).
      retry: (failureCount, error) => {
        if (error instanceof ApiError && error.status >= 400 && error.status < 500) {
          return false;
        }
        return failureCount < 1;
      },
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: 0,
    },
  },
});

/** Wraps a lazy element in Suspense. The fallback is the branded
 *  PageLoader — the same screen the auth-resolution state shows, so
 *  every "page not ready yet" moment looks identical. PageLoader
 *  stays invisible for its first 150ms, so a warm chunk cache (the
 *  common case after first visit) never flashes a loader frame. */
function lazyRoute(element: ReactNode): ReactNode {
  return <Suspense fallback={<PageLoader label="Loading page" />}>{element}</Suspense>;
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          {/* ThemeProvider sits *inside* AuthProvider so it can read
              the user's saved theme from the session payload. The
              pre-paint inline script in `index.html` already set a
              sensible data-theme from localStorage before React even
              parses, so this provider is responsible for the
              server-reconciliation pass and the live "system" media
              query — not for the first paint. */}
          <ThemeProvider>
          <ConfirmDialogProvider>
          <AppErrorBoundary>
          {/* ⌘K / Ctrl+K menu for signed-in users; the workspace header's
              Search button opens it through the same context. */}
          <CommandMenuProvider>
          <Routes>
            <Route element={<AppLayout />}>
              {/* Public routes */}
              <Route path="/login" element={<LoginPage />} />
              <Route
                path="/forgot-password"
                element={lazyRoute(<ForgotPasswordPage />)}
              />
              <Route
                path="/auth/magic"
                element={lazyRoute(<ConsumeMagicLinkPage />)}
              />
              <Route
                path="/auth/invite"
                element={lazyRoute(<AcceptInvitationPage />)}
              />
              <Route
                path="/auth/reset-password"
                element={lazyRoute(<ResetPasswordPage />)}
              />

              {/* Authenticated routes */}
              <Route
                path="/"
                element={
                  <ProtectedRoute>
                    <FormPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/account"
                element={
                  <ProtectedRoute>{lazyRoute(<AccountPage />)}</ProtectedRoute>
                }
              />
              <Route
                path="/booked"
                element={
                  <ProtectedRoute permission="viewBookedTrips">
                    <BookedTripsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin"
                element={<Navigate to="/admin/users" replace />}
              />
              <Route
                path="/admin/users"
                element={
                  <ProtectedRoute allow={["admin", "super_admin"]}>
                    {lazyRoute(<AdminUsersPage />)}
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/bookings"
                element={
                  <ProtectedRoute allow={["admin", "super_admin"]}>
                    {lazyRoute(<AdminBookingsPage />)}
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/departments"
                element={
                  <ProtectedRoute allow={["admin", "super_admin"]}>
                    {lazyRoute(<AdminDepartmentsPage />)}
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/locations"
                element={
                  <ProtectedRoute allow={["admin", "super_admin"]}>
                    {lazyRoute(<AdminLocationsPage />)}
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/members"
                element={
                  <ProtectedRoute allow={["admin", "super_admin"]}>
                    {lazyRoute(<AdminMembersPage />)}
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/audit"
                element={
                  <ProtectedRoute allow={["admin", "super_admin"]}>
                    {lazyRoute(<AdminAuditPage />)}
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/reports"
                element={
                  <ProtectedRoute allow={["admin", "super_admin"]}>
                    {lazyRoute(<AdminReportsPage />)}
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/permissions"
                element={
                  <ProtectedRoute allow={["super_admin"]}>
                    {lazyRoute(<AdminPermissionsPage />)}
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/tms-cleanup"
                element={
                  <ProtectedRoute allow={["admin", "super_admin"]} royalTeamOnly>
                    {lazyRoute(<AdminTmsCleanupPage />)}
                  </ProtectedRoute>
                }
              />
              {/* Royal-internal Billing review. Same gate as TMS
                  Cleanup / the TripLink sync section: admin/super_admin
                  AND Royal department (royalTeamOnly). Blocks direct
                  navigation for non-Royal users. */}
              <Route
                path="/admin/billing"
                element={
                  <ProtectedRoute allow={["admin", "super_admin"]} royalTeamOnly>
                    {lazyRoute(<AdminBillingPage />)}
                  </ProtectedRoute>
                }
              />

              <Route path="*" element={<Navigate to="/" replace />} />
            </Route>
          </Routes>
          </CommandMenuProvider>
          {/* Global toast outlet. Mounted once at the root so any
              feature can call `toast.success(...)` / `toast.error(...)`
              from anywhere in the tree. We pin it bottom-center on
              mobile so it doesn't collide with the AccountMenu in
              the upper right; sm+ screens use the default top-right
              corner where it's least likely to overlap content. */}
          <ThemedToaster />
          </AppErrorBoundary>
          {/* First-time theme picker — opens automatically when
              ThemeProvider's `needsFirstTimePick` is true (server
              returned `themePreference: "unset"`). Renders no DOM
              for users who've already picked, so it's free for
              everyone past the first launch. */}
          <FirstTimeThemeDialog />
          </ConfirmDialogProvider>
          </ThemeProvider>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

/**
 * Tiny wrapper that bridges sonner's `theme` prop to our
 * `ThemeProvider.resolved` value. Lives here rather than in
 * `theme/` because (a) it's a one-liner specific to the global
 * Toaster outlet and (b) keeping the Toaster mount adjacent to its
 * configuration makes the App tree easier to read.
 *
 * Why this exists: the Toaster was previously hardcoded
 * `theme="dark"`, which meant `richColors` toasts (success / error
 * etc.) ignored the user's light-mode pick and rendered with the
 * dark palette — visually inconsistent and, more importantly, the
 * dark green/red/amber fills against a white page were eye-pulling
 * in a bad way. Sonner re-themes itself on the fly when the prop
 * changes, so the existing toasts adapt instantly to the user
 * flipping themes via the AccountMenu.
 */
function ThemedToaster() {
  const { resolved } = useTheme();
  return (
    <Toaster
      position="bottom-center"
      offset={16}
      duration={4000}
      closeButton
      richColors
      theme={resolved}
      toastOptions={{
        classNames: {
          toast:
            "border border-foreground/15 bg-card text-foreground shadow-2xl",
        },
      }}
    />
  );
}
